import type { ReactNode } from 'react';
import { Spinner } from '@salonomy/ui';
import { useAuth } from '../lib/auth.js';
import { AppShell } from './AppShell.js';
import { Login } from './Login.js';

function Splash() {
  return (
    <div className="splash">
      <div className="splash__mark">
        Salonomy<span style={{ color: 'var(--slm-cobalt)' }}>.</span>
      </div>
      <Spinner size={20} />
    </div>
  );
}

/**
 * WS1 account-recovery — the PRE-session routes that must render their own page for an ANONYMOUS
 * visitor (with a token in the URL) instead of being swallowed by the bare Login. These are matched
 * against the basepath-relative pathname below; the literals mirror the routes registered in
 * router.tsx. '/reset-password' MUST match the API's emailed link path.
 */
const ANON_PATHS: ReadonlySet<string> = new Set([
  '/accept-invite',
  '/forgot-password',
  '/reset-password',
]);

/** True when the current URL is one of the pre-session recovery routes (basepath-aware). */
function isAnonPath(): boolean {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) path = path.slice(base.length);
  if (!path.startsWith('/')) path = `/${path}`;
  return ANON_PATHS.has(path.replace(/\/$/, '') || '/');
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <Splash />;
  // An anonymous visitor on a recovery route gets that page (each renders its own full-page panel),
  // not the bare Login. Everywhere else, anonymous still means sign in.
  if (status === 'anon') return isAnonPath() ? <>{children}</> : <Login />;
  return <AppShell>{children}</AppShell>;
}
